import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Storage } from '@ionic/storage';
import moment from 'moment';
import { LocalStorage } from '../local-storage/local-storage';
import { ISynchronisationService } from '../synchronisation/iSynchronisation-service';
import { UserService } from '../auth/user.service';
import { NetworkProvider } from '../network/network';
import { Events } from '../events/events.provider';
import { AlertController, LoadingController } from '@ionic/angular';

import {
  ActiviteInterne,
  CrActiviteInterne,
  EtatActiviteInterne,
} from '../../models/activite-interne/activite-interne.model';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ChronosProvider, TypeAction } from '../chronos/chronos.provider';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { ActiviteInterneProvider } from './activite-interne.provider';
import { ActiviteEnCours, ActiviteEnCoursService } from '../../components/activite-en-cours/activite-en-cours.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ActiviteInterneService extends LocalStorage implements ISynchronisationService {
  private logger: LoggerWrapper;

  constructor(
    private activiteInterneProvider: ActiviteInterneProvider,
    private userService: UserService,
    protected override storage: Storage,
    protected override network: NetworkProvider,
    private events: Events,
    private chronosProvider: ChronosProvider,
    private loadingCtrl: LoadingController,
    private loggerFactory: LoggerFactory,
    private activiteEnCoursService: ActiviteEnCoursService,
    private alertCtrl: AlertController,
    private translate: TranslateService
  ) {
    super();
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Synchronisation des CR
   */
  public synchronisation(): Observable<number> {
    if (!this.network.isConnected()) {
      return fromPromise(this.getCr()).pipe(map((crs) => crs.length));
    }
    return fromPromise(this.synchroCrNonEnvoye()).pipe(
      switchMap(() => this.getCr()),
      map((result) => result.length)
    );
  }

  public async synchroCrNonEnvoye(): Promise<CrActiviteInterne[]> {
    let crstoSend: CrActiviteInterne[] = await this.getCrLocalNonRemonte();
    if (!crstoSend) {
      return [];
    }
    let crsNonEnvoyes: CrActiviteInterne[] = [];

    for (let cr of crstoSend) {
      if (cr.etat !== EtatActiviteInterne.ErreurEnvoi) {
        try {
          if (cr.id_activite_interne === null) {
            cr.id_activite_interne = await this.synchroActiviteNonEnvoye(cr.idTempo);
          }
          cr = await this.saveCrLocalNonRemonte(cr);
        } catch (error) {
          this.logger.error(error, 'Erreur synchroCrNonEnvoye', cr);
          cr.etat = EtatActiviteInterne.ErreurServeur;
        }
      }
      if (cr.etat !== EtatActiviteInterne.Envoye) {
        crsNonEnvoyes.push(cr);
      }
    }
    await this.storage.set(this.CR_ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY, crsNonEnvoyes);
    return crstoSend;
  }

  private async synchroActiviteNonEnvoye(idTempoCr: string): Promise<string> {
    let activitesNonEnvoyes: ActiviteInterne[] =
      (await this.storage.get(this.ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY)) || [];
    const localAct = activitesNonEnvoyes.find((act) => act.id_tempo === idTempoCr) || {
      id_intervenant: this.userService.getId(),
      id_tempo: null,
      matricule: this.userService.getMatricule(),
      planification_date_debut: null,
      planification_date_fin: null,
      type_activite_interne_planifiee: null,
    };
    let idActivite = await this.saveActivite(localAct);
    // On retire l'activite qu'on vient d'envoyer de la liste des activites non envoyees
    const activites = activitesNonEnvoyes.filter((activite) => activite.id_tempo !== idTempoCr);
    await this.storage.set(this.ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY, activites);
    return idActivite;
  }

  public async startActiviteInterne(isRenfort: boolean) {
    const otEnCours: ActiviteEnCours = await this.activiteEnCoursService.getActiviteEnCours();
    if (otEnCours) {
      this.alertCtrl
        .create({
          message: this.translate.instant('GLOBAL.TOO_MANY_OTS_FOR_ACTIVITE'),
          buttons: [this.translate.instant('GLOBAL.OK')],
        })
        .then((alert) => alert.present());
    } else if (this.chronosProvider.hasTooMuchChronos()) {
      await this.chronosProvider.alertTooMuchChronos();
    } else {
      const loading = await this.loadingCtrl.create();
      await loading.present();

      try {
        let id = await this.startActivite();
        await this.chronosProvider.addChronoActiviteInterne(id, null, null, TypeAction.Intervention, isRenfort);
      } catch (error) {
        this.logger.error(error, `Erreur startActiviteInterne with isRenfort = ${isRenfort}`);
      }
      await loading.dismiss();
    }
  }

  /**
   * Enregistre l'activité sur le serveur
   * @param activite activité interne à enregistrer
   */
  public async saveActivite(activite: ActiviteInterne): Promise<string> {
    let uuidResponse = await this.activiteInterneProvider.postActiviteInterne(activite);
    if (uuidResponse === null || uuidResponse.id === null) {
      let activites: ActiviteInterne[] = (await this.storage.get(this.ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY)) || [];
      const sameActivite = activites.find(
        (act) =>
          act.id_tempo === activite.id_tempo &&
          act.type_activite_interne_planifiee === activite.type_activite_interne_planifiee
      );
      if (!sameActivite) {
        activites.push(activite);
      }
      await this.storage.set(this.ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY, activites);
    }
    return uuidResponse.id;
  }

  /** Enregistre le compte rendu d'une activité interne
   * @param cr Le CR à enregistrer
   */
  public async saveCr(cr: CrActiviteInterne): Promise<string> {
    return await this.activiteInterneProvider.postCrActiviteInterne(cr).then((uuidResponse) => {
      this.events.publish('cr_list_update');
      return uuidResponse.id;
    });
  }

  /**
   * Récupere la totalité des CR d'activités interne pour un intervenant
   */
  public async getCr(): Promise<CrActiviteInterne[]> {
    let crs: CrActiviteInterne[];
    const idIntervenant = this.userService.getId();
    if (this.network.isConnected()) {
      try {
        let saisi_apres_date = moment().subtract(5, 'weeks').subtract(1, 'day').startOf('day').toISOString();
        let saisi_avant_date = moment().add(1, 'day').toISOString();
        crs = await this.activiteInterneProvider.getCr(idIntervenant, saisi_apres_date, saisi_avant_date);
        await this.storage.set(this.CR_ACTIVITE_INTERNE_LOCAL_KEY, crs);
      } catch (error) {
        this.logger.error(error, 'Erreur getCr');
        return (await this.storage.get(this.CR_ACTIVITE_INTERNE_LOCAL_KEY)) || [];
      }
    } else {
      crs = (await this.storage.get(this.CR_ACTIVITE_INTERNE_LOCAL_KEY)) || [];
    }

    let allCrs = crs.concat(await this.getCrLocalNonRemonte());
    return allCrs.map((cr) => Object.assign(new CrActiviteInterne(), cr));
  }

  /**
   * Enregistre le compte rendu d'une activité interne
   * @param cr Le CR à enregistrer
   */
  private async saveCrLocalNonRemonte(cr: CrActiviteInterne): Promise<CrActiviteInterne> {
    try {
      await this.saveCr(cr);
      cr.etat = EtatActiviteInterne.Envoye;
    } catch (e) {
      this.logger.error(e, 'Erreur saveCrLocalNonRemonte', cr);
      if (e.status === 500) {
        cr.etat = EtatActiviteInterne.ErreurServeur;
      } else if (e.status >= 400 && e.status < 500) {
        cr.etat = EtatActiviteInterne.ErreurEnvoi;
      }
    }
    return cr;
  }

  private startActivite(): Promise<string> {
    return this.saveActivite({
      id_intervenant: this.userService.getId(),
      id_tempo: null,
      matricule: this.userService.getMatricule(),
      planification_date_debut: null,
      planification_date_fin: null,
      type_activite_interne_planifiee: null,
    });
  }

  /**
   * Sauvegarde les CR en local quand il n'y a pas de connexion
   * @param cr
   */
  public async saveCrLocal(cr: CrActiviteInterne) {
    cr.etat = EtatActiviteInterne.EnCours;
    let crs = (await this.storage.get(this.CR_ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY)) || [];
    crs.push(cr);
    await this.storage.set(this.CR_ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY, crs);
  }

  /**
   * Récupere les Comptes rendus non remontés au serveur
   */
  private async getCrLocalNonRemonte(): Promise<CrActiviteInterne[]> {
    return (await this.storage.get(this.CR_ACTIVITE_INTERNE_LOCAL_NON_REMONTE_KEY)) || [];
  }
}
